import { useCallback, useEffect, useState } from "react";
import { Activity, Clock, LogOut, ShieldCheck, Users, Wallet } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, getApiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { StatCard } from "@/components/StatCard";
import { AddCustomerDialog } from "@/components/AddCustomerDialog";
import { PendingApprovals } from "@/components/PendingApprovals";
import { CustomersTable } from "@/components/CustomersTable";

const Header = ({ email, onLogout }) => (
  <header className="border-b border-slate-200 bg-white">
    <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-slate-900" />
        <span className="font-display text-lg font-bold text-slate-900">ThankF</span>
        <Badge variant="outline" className="ml-3 label-overline">Admin</Badge>
      </div>
      <div className="flex items-center gap-3">
        <span className="hidden sm:block text-sm text-slate-600">{email}</span>
        <Button variant="outline" size="sm" data-testid="admin-logout-btn" onClick={onLogout} className="gap-2">
          <LogOut className="h-4 w-4" /> Sign out
        </Button>
      </div>
    </div>
  </header>
);

const Stats = ({ stats }) => (
  <section className="grid grid-cols-1 sm:grid-cols-4 gap-6">
    <StatCard label="Customers" value={stats.customer_count} mono icon={Users} testid="stat-customers" />
    <StatCard label="Total balance" value={formatINR(stats.total_balance)} mono icon={Wallet} testid="stat-total-balance" />
    <StatCard label="Transactions" value={stats.transaction_count} mono icon={Activity} testid="stat-transactions" />
    <StatCard label="Pending approval" value={stats.pending_count || 0} mono icon={Clock} testid="stat-pending" />
  </section>
);

const EMPTY_STATS = { customer_count: 0, total_balance: 0, transaction_count: 0, pending_count: 0 };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [pendingBusy, setPendingBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([api.get("/admin/customers"), api.get("/admin/stats")]);
      setCustomers(c.data.customers || []);
      setStats(s.data);
    } catch (e) {
      toast.error(getApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (cid, action) => {
    setPendingBusy(cid);
    try {
      await api.post(`/admin/customers/${cid}/${action}`);
      toast.success(action === "approve" ? "Customer approved" : "Customer rejected");
      await load();
    } catch (err) {
      toast.error(getApiError(err));
    } finally {
      setPendingBusy(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header email={user?.email} onLogout={logout} />
      <main className="max-w-7xl mx-auto px-6 py-10 space-y-10" data-testid="admin-dashboard">
        <section>
          <p className="label-overline text-slate-500">Overview</p>
          <h1 className="font-display text-4xl font-extrabold text-slate-900 mt-2">Customer balances</h1>
          <p className="text-slate-600 mt-2 max-w-2xl">
            Manage your customer wallets — add new customers, credit or debit balances, and review transaction history.
          </p>
        </section>
        <Stats stats={stats} />
        <PendingApprovals
          pending={customers.filter((c) => c.status === "pending")}
          busyId={pendingBusy}
          onApprove={(cid) => decide(cid, "approve")}
          onReject={(cid) => decide(cid, "reject")}
        />
        <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <p className="label-overline text-slate-500">Customers</p>
              <h2 className="font-display text-xl font-bold text-slate-900 mt-1">All registered customers</h2>
            </div>
            <AddCustomerDialog onCreated={load} />
          </div>
          <CustomersTable customers={customers} loading={loading} />
        </section>
      </main>
    </div>
  );
}
